<script setup>
import { ref, watch, onMounted, computed } from "vue";
import Sortable from "sortablejs";
import "@vueup/vue-quill/dist/vue-quill.snow.css";
import { ChaptersClient } from "@/services/ChaptersClient";
import { LessonsClient } from "@/services/LessonsClient";
import { QuizzesClient } from "@/services/QuizzesClient";
import Swal from "sweetalert2";
import { useRoute} from "vue-router"; 
const route = useRoute();

const props = defineProps({
  chapterId: {
    type: Number,
    default: null,
  },
  existingChapter: {
    type: Object,
    default: null,
  },
});

const courseChapters = ref([]); // Chapters in the course
const chapterQuizzes = ref([]); // Quizzes in this chapter
const reqOptions = ref([]);      // Options for the <select>
const selectedReq = ref("");  // v-model
const existingPrereq = ref(null); // store the current prerequisite

const quizzesClient = new QuizzesClient();
const chaptersClient = new ChaptersClient();
const lessonsClient = new LessonsClient();
const emit = defineEmits([
  "save-chapter",
  "delete-chapter",
  "create-chapter",
  "units-reordered", // Add this new emit
]);
const chapterData = ref({
  id: null,
  titulo: "",
  es_gratuito: 0,
  orden: 0,
  objetivos: "",
  isNew: true,
  lessons: [],
  quizzes: [],
  reqType: "none" // Set "none" as the default value
});

const isLoading = ref(false);
const isUnitsLoading = ref(false); // Combined loading state for lessons and quizzes
const hasFetchedUnits = ref(false); // Track if we've completed the initial fetch

// Fetch data when component mounts
onMounted(async () => {
  if (props.chapterId) {
    await fetchUnits();
  }
  initSortable();
  
});

const sortedUnits = computed(() => {
  if (!hasFetchedUnits.value) return [];

  const lessons = chapterData.value.lessons || [];
  const quizzes = chapterData.value.quizzes || [];

  const lessonsWithType = lessons.map((lesson) => ({
    ...lesson,
    tipo: "leccion",
  }));

  const quizzesWithType = quizzes.map((quiz) => ({
    ...quiz,
    tipo: "quiz",
  }));

  return [...lessonsWithType, ...quizzesWithType].sort(
    (a, b) => a.orden - b.orden
  );
});

// Combined fetch for both lessons and quizzes
const fetchUnits = async () => {
  try {
    isUnitsLoading.value = true;
    hasFetchedUnits.value = false;

    await Promise.all([fetchLessons(), fetchQuizzes()]);

    hasFetchedUnits.value = true;
  } catch (error) {
    console.error("Error fetching units:", error);
    Swal.fire(
      "Error",
      "No se pudieron cargar las lecciones y quizzes",
      "error"
    );
  } finally {
    isUnitsLoading.value = false;
  }
};

const fetchLessons = async () => {
  try {
    const response = await lessonsClient.getAllLessonsByChapter(
      props.chapterId
    );
    chapterData.value.lessons = Array.isArray(response) ? response : [];
  } catch (error) {
    console.error("Error fetching lessons:", error);
    chapterData.value.lessons = [];
    throw error; // Re-throw to be caught in fetchUnits
  }
};

const fetchQuizzes = async () => {
  try {
    const response = await quizzesClient.getAllQuizzesByChapter(
      props.chapterId
    );
    chapterData.value.quizzes = Array.isArray(response) ? response : [];
  } catch (error) {
    console.error("Error fetching quizzes:", error);
    chapterData.value.quizzes = [];
    throw error; // Re-throw to be caught in fetchUnits
  }
};

const fetchPrerequisites = async () => {
  if (!props.chapterId) return;
  try {
    const data = await chaptersClient.getPrerequisites(props.chapterId);
    
    // If no prerequisites found, set to "none" and clear any existing
    if (!data || data.length === 0) {
      existingPrereq.value = null;
      chapterData.value.reqType = "none";
      selectedReq.value = "";
      return;
    }
    
    existingPrereq.value = data[0] || null;
    if (existingPrereq.value) {
      selectedReq.value = existingPrereq.value.requisito_id;
      chapterData.value.reqType = existingPrereq.value.requisito_tipo === "capitulo" ? "chapter" : "quiz";
      await updateReqOptions();

    }
  } catch (err) {
    console.error("Error fetching prerequisites:", err);
    // On error, also set to "none" as default
    existingPrereq.value = null;
    chapterData.value.reqType = "none";
    selectedReq.value = "";
  }
};

const updateReqOptions = async () => {
  const courseId = computed(() => route.params.id);
  if (chapterData.value.reqType === "chapter") {
    const chapters = await chaptersClient.getAllChaptersByCourse(courseId.value);
    reqOptions.value = chapters.map(c => ({ value: c.id, text: c.titulo }));
  } else if (chapterData.value.reqType === "quiz") {
    reqOptions.value = chapterData.value.quizzes.map(q => ({ value: q.id, text: q.titulo }));
  } else {
    reqOptions.value = [];
    selectedReq.value = "";
  }
};

const savePrerequisite = async () => {
  if (!props.chapterId) return;

  try {
    if (chapterData.value.reqType === "none") {
      // Delete existing prerequisite if any
      if (existingPrereq.value) {
        await chaptersClient.deletePrerequisite(props.chapterId, existingPrereq.value.id);
        existingPrereq.value = null;
      }
    } else {
      const payload = chapterData.value.reqType === "chapter"
        ? { requisito_tipo: "capitulo", requisito_id: Number(selectedReq.value), regla: "COMPLETADO" }
        : { requisito_tipo: "cuestionario", requisito_id: Number(selectedReq.value), regla: "APROBADO", nota_minima: 80 };

      if (existingPrereq.value) {
        // update
        await chaptersClient.updatePrerequisite(props.chapterId, existingPrereq.value.id, payload);
      } else {
        // create
        await chaptersClient.addPrerequisite(props.chapterId, payload);
      }
      // Refresh after save
      await fetchPrerequisites();
    }

  } catch (err) {
    console.error("Error saving prerequisite:", err);
    Swal.fire("Error", "No se pudo guardar el requisito", "error");
  }
};

watch(() => chapterData.value.reqType, updateReqOptions);

const fetchCourseChapters = async () => {
  try {
    const courseId = computed(() => route.params.id);
    console.log(courseId.value);
    
    const response = await chaptersClient.getAllChaptersByCourse(courseId.value);
    console.log(response);
    
    courseChapters.value = Array.isArray(response) ? response : [];
  } catch (error) {
    console.error("Error fetching course chapters:", error);
    courseChapters.value = [];
  }
};

// Update chapter
const updateChapter = async () => {
  try {
    isLoading.value = true;

    const updateData = {
      title: chapterData.value.titulo, // Changed to match parent expectation
      isFree: chapterData.value.accessType === "free", // Changed to boolean
    };
    savePrerequisite();
    console.log("Update data:", updateData);

    emit("save-chapter", props.chapterId, updateData);
    Swal.fire("¡Guardado!", "El capítulo ha sido actualizado", "success");
  } catch (error) {
    console.error("Error updating chapter:", error);
    Swal.fire("Error", "No se pudo actualizar el capítulo", "error");
  } finally {
    isLoading.value = false;
  }
};

// Delete chapter
const deleteChapter = async () => {
  const result = await Swal.fire({
    title: "¿Eliminar permanentemente?",
    text: "Todos los datos asociados serán eliminados.",
    icon: "warning",
    showCancelButton: true,
    confirmButtonColor: "#dc3545", // Red
    cancelButtonColor: "#6c757d", // Gray
    confirmButtonText: "Eliminar",
    cancelButtonText: "Cancelar",
    buttonsStyling: false,
    customClass: {
      actions: "grid grid-cols-2 gap-2 w-full", // Key change for 2 columns
      confirmButton: "btn btn-danger w-full",
      cancelButton: "btn btn-secondary w-full",
    },
    reverseButtons: false, // Keep original order
  });

  if (result.isConfirmed) {
    emit("delete-chapter", props.chapterId); // Just emit the ID
  }
};

const reorderUnits = async (newOrderContainer) => {
  console.log("Starting reorderUnits", { newOrderContainer });
  try {
    const newOrder = Array.from(newOrderContainer.children).map(
      (el, index) => ({
        id: parseInt(el.dataset.id),
        orden: index + 1,
        tipo: el.dataset.tipo,
      })
    );

    // Separate lessons and quizzes for the parent component
    const lessons =
      chapterData.value.lessons
        ?.map((lesson) => {
          const orderItem = newOrder.find(
            (item) => item.tipo === "leccion" && item.id === lesson.id
          );
          return orderItem ? { ...lesson, orden: orderItem.orden } : lesson;
        })
        .sort((a, b) => a.orden - b.orden) || [];

    const quizzes =
      chapterData.value.quizzes
        ?.map((quiz) => {
          const orderItem = newOrder.find(
            (item) => item.tipo === "quiz" && item.id === quiz.id
          );
          return orderItem ? { ...quiz, orden: orderItem.orden } : quiz;
        })
        .sort((a, b) => a.orden - b.orden) || [];

    // Emit to parent with properly separated arrays
    emit("units-reordered", {
      chapterId: props.chapterId,
      lessons,
      quizzes,
    });

    // API call remains the same
    const apiPayload = newOrder.map((item) => ({
      id: item.id,
      orden: item.orden,
      tipo: item.tipo,
    }));
    await chaptersClient.reorderUnits(apiPayload);
  } catch (error) {
    console.error("Error reordering units:", error);
    await Promise.all([fetchLessons(), fetchQuizzes()]);
    Swal.fire("Error", "No se pudo guardar el nuevo orden", "error");
  }
};

const initSortable = () => {
  const el = document.getElementById("lessonsList");
  if (el) {
    new Sortable(el, {
      animation: 150,
      ghostClass: "sortable-ghost",
      handle: ".drag-handle",
      onEnd: async (evt) => {
        console.log("Sortable onEnd event:", {
          oldIndex: evt.oldIndex,
          newIndex: evt.newIndex,
          from: evt.from,
          to: evt.to,
        });
        await reorderUnits(evt.to);
      },
    });
  }
};

watch(
  () => props.existingChapter,
  (newVal) => {
    if (newVal) {
      chapterData.value = {
        ...newVal,
        accessType: newVal.es_gratuito ? "free" : "pay",
        isNew: false,
        lessons: Array.isArray(newVal.lessons) ? newVal.lessons : [],
        quizzes: Array.isArray(newVal.quizzes) ? newVal.quizzes : [],
        reqType: "none"

      };
      fetchPrerequisites();

    }
  },
  { immediate: true }
);

watch(
  () => chapterData.value.reqType,
  async (newType) => {
    if (newType === "chapter") {
      // Chapters as options
      await fetchCourseChapters();
      reqOptions.value = courseChapters.value.map((chap) => ({
        value: chap.id,
        text: chap.titulo,
      }));
      
      // Don't reset selection if we already have one
      if (!selectedReq.value || !reqOptions.value.some(opt => opt.value == selectedReq.value)) {
        selectedReq.value = "";
      }
    } else if (newType === "quiz") {
      // Quizzes as options - make sure we have the latest quizzes
      await fetchQuizzes();
      reqOptions.value = chapterData.value.quizzes.map((quiz) => ({
        value: quiz.id,
        text: quiz.titulo,
      }));
      
      // Don't reset selection if we already have one
      if (!selectedReq.value || !reqOptions.value.some(opt => opt.value == selectedReq.value)) {
        selectedReq.value = "";
      }
    } else {
      reqOptions.value = [];
      selectedReq.value = "";
    }
  }
);

</script>

<template>
  <div class="card card2">
    <div class="card-header d-flex pb-2 flex-column flex-md-row">
      <div class="col-12 col-md-8">
        <h6 class="font-weight-bolder mb-0">
          <i class="fas fa-book-open pe-2"></i>
          {{ chapterData.titulo || "Nuevo Capítulo" }}
        </h6>
        <p class="text-sm mb-0">Edita este capítulo</p>
      </div>
      <div
        class="col-12 col-md-4 gap-2 d-flex align-items-center justify-content-between"
      >
        <button
          class="mt-2 mb-0 ms-2 btn btn-outline-dark w-70 mt-lg-0"
          @click="updateChapter"          
          :disabled="isLoading"
        >
          <span
            v-if="isLoading"
            class="spinner-border spinner-border-sm me-1"
          ></span>
          <i v-else class="far fa-save pe-2"></i>
          Guardar
        </button>
        <button
          v-if="!chapterData.isNew"
          type="button"
          class="btn-sm px-4 mt-2 ms-0 mb-0 btn btn-dark mt-lg-0"
          @click="deleteChapter"
          :disabled="isLoading"
        >
          <i class="fas fa-trash"></i>
        </button>
      </div>
    </div>

    <div class="card-body pt-1">
      <div class="row">
        <div class="col-12 col-md-4 mb-3">
          <div class="mb-2">
            <label class="m-0">Tipo de Acceso</label>
          </div>

          <div class="btn-group" role="group" aria-label="Tipo de Acceso">
            <!-- Free Access -->
            <input
              type="radio"
              class="btn-check"
              name="access"
              id="free"
              value="free"
              v-model="chapterData.accessType"
              :true-value="1"
              :false-value="0"
            />
            <label
              class="btn btn-sm mb-0 btn-outline-dark px-3 py-2 position-relative"
              for="free"
              :class="{ active: chapterData.es_gratuito }"
              data-bs-toggle="tooltip"
              data-bs-placement="top"
              title="Acceso gratuito para todos los estudiantes"
            >
              Gratis
            </label>

            <!-- Paid Access -->
            <input
              type="radio"
              class="btn-check"
              name="access"
              id="pay"
              value="pay"
              v-model="chapterData.accessType"
              :true-value="0"
              :false-value="1"
            />
            <label
              class="btn btn-sm mb-0 btn-outline-dark px-3 py-2 position-relative"
              for="pay"
              :class="{ active: !chapterData.es_gratuito }"
              data-bs-toggle="tooltip"
              data-bs-placement="top"
              title="Acceso solo para estudiantes que hayan comprado el curso"
            >
              <i class="fas fa-star pe-2"></i>Pago
            </label>
          </div>
        </div>

        <div class="col-12 col-md-8 mb-2 mb-md-4">
          <div class="d-flex justify-content-between align-items-center">
            <label>Título <span class="text-danger">*</span></label>
            <small class="text-muted ms-4">(máx. 100 caracteres)</small>
          </div>
          <input
            class="form-control"
            type="text"
            placeholder="Título del capítulo"
            v-model="chapterData.titulo"
            :disabled="isLoading"
            maxlength="100"
          />
        </div>
      </div>

      <div class="row">
        <div class="col-12 col-md-6 mb-2 mb-md-4">
          <div class="mb-2">
            <label class="m-0">Requisito</label>
          </div>
         <div class="btn-group" role="group" aria-label="Tipo de Acceso">
          <input type="radio" class="btn-check" name="req" id="none" value="none" v-model="chapterData.reqType" />
          <label class="btn btn-sm mb-0 btn-outline-dark px-3 py-2" for="none">
            <i class="fas fa-ban pe-1"></i>
            Ninguno
          </label>

          <input type="radio" class="btn-check" name="req" id="chapter" value="chapter" v-model="chapterData.reqType" />
          <label class="btn btn-sm mb-0 btn-outline-dark px-3 py-2" for="chapter">
            <i class="fas fa-book pe-1"></i>
            Capítulo
          </label>

          <input type="radio" class="btn-check" name="req" id="quiz" value="quiz" v-model="chapterData.reqType" />
          <label class="btn btn-sm mb-0 btn-outline-dark px-3 py-2" for="quiz">
            <i class="fas fa-question-circle pe-1"></i>
            Quiz
          </label>
        </div>
        </div>

        <div class="col-12 col-md-6 mb-2 mb-md-4" v-if="chapterData.reqType !== 'none'">
          <div class="mb-2">
            <label class="m-0">Seleccione un requisito <span class="text-danger">* </span></label>
          </div>
          <select v-model="selectedReq" class="form-control" required>
            <option value="">-</option>
            <option
              v-for="option in reqOptions"
              :key="option.value"
              :value="option.value"
            >
              {{ option.text }}
            </option>
          </select>
        </div>
      </div>

      <div class="alert alert-light mt-3 z-index" v-if="chapterData.reqType !== 'none'">
          <i
            class="fas fa-lock text-info"           
          ></i>
          <span class="ms-2">
            <template v-if="chapterData.reqType == 'chapter'">
              Se requiere que completar el  <strong> Capítulo</strong> seleccionado para acceder a este contenido.
            </template>
            <template v-if="chapterData.reqType == 'quiz'">
              Se requiere que aprobar el  <strong> Quiz</strong> seleccionado para acceder a este contenido.
            </template>
          </span>
        </div>

      <hr class="horizontal dark mt-1 mb-1" />

      <!-- Sortable Lessons List -->
      <div class="mb-4">
        <label class="m-0">Lecciones y Quizzes</label>
        <p class="text-sm mb-2 mb-md-4">
          Arrastra y suelta para re ordenar el contenido del capítulo.
        </p>

        <!-- Loading state -->
        <div v-if="isUnitsLoading" class="text-center py-4">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Cargando...</span>
          </div>
          <p class="mt-2 text-muted">Cargando contenido...</p>
        </div>

        <!-- Empty state -->
        <div
          v-else-if="hasFetchedUnits && sortedUnits.length === 0"
          class="text-center py-4 bg-light rounded"
        >
          <i class="fas fa-inbox fa-2x text-muted mb-3"></i>
          <h6 class="text-muted">No hay lecciones ni quizzes</h6>
          <p class="small text-muted">
            Añade tu primera lección o quiz para comenzar
          </p>
        </div>

        <!-- Content -->
        <div
          v-else
          id="lessonsList"
          class="mt-md-3 mt-0"
          style="max-height: 55vh; overflow-y: auto"
        >
          <div
            v-for="unit in sortedUnits"
            :key="`${unit.tipo}-${unit.id}`"
            class="card mb-2 bg-light"
            :data-id="unit.id"
            :data-tipo="unit.tipo"
          >
            <div class="card-body px-3 py-2 py-md-3">
              <div class="row align-items-center">
                <!-- Left Column (Drag handle and unit icon) -->
                <div class="col-10 col-md-11 d-flex align-items-center">
                  <div class="drag-handle me-2 me-md-3" style="cursor: move">
                    <i class="fas fa-grip-vertical text-muted"></i>
                  </div>
                  <div class="d-flex align-items-center">
                    <div class="me-2 me-md-3">
                      <i
                        v-if="unit.tipo === 'leccion'"
                        class="fas fa-book text-primary"
                        title="Lección"
                      ></i>
                      <i
                        v-else
                        class="fas fa-question-circle text-success"
                        title="Evaluación"
                      ></i>
                    </div>
                    <h6 class="mb-0 text-md-lg text-sm text-truncate">
                      {{ unit.titulo }}
                    </h6>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style>
.card2 {
  min-height: auto;
}

.sortable-ghost {
  background: #f8f9fa;
  opacity: 0.7;
  border: 1px dashed #ccc;
}

.drag-handle {
  cursor: move;
}

.btn-group .btn.active {
  background-color: #ffffff;
  color: #344767;
}

#lessonsList {
  min-height: 100px;
}

/* Smooth transitions for loading states */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.spinner-border {
  width: 1rem;
  height: 1rem;
  border-width: 0.2em;
}
</style>
